import { useState, useCallback } from 'react';

/**
 * Custom Hook für Fragen-Editor State
 * 
 * Verwaltet:
 * - Aktuell bearbeitete Frage
 * - Edit Mode State
 * - Speichern/Abbrechen
 */
export function useQuestionEditor(questions, setQuestions) {
  const [editingIndex, setEditingIndex] = useState(null);
  const [editedQuestion, setEditedQuestion] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  /**
   * Start editing a question
   */
  const startEditing = useCallback((index) => {
    if (index < 0 || index >= questions.length) return false;
    
    setEditingIndex(index);
    setEditedQuestion({ ...questions[index] });
    setHasUnsavedChanges(false);
    return true;
  }, [questions]);
  
  /**
   * Update the edited question
   */
  const updateEditedQuestion = useCallback((updates) => {
    setEditedQuestion(prev => {
      if (!prev) return null;
      const updated = typeof updates === 'function' ? updates(prev) : { ...prev, ...updates };
      setHasUnsavedChanges(true);
      return updated;
    });
  }, []);
  
  /**
   * Update a specific field of the edited question
   */
  const updateField = useCallback((field, value) => {
    setEditedQuestion(prev => {
      if (!prev) return null;
      setHasUnsavedChanges(true);
      return { ...prev, [field]: value };
    });
  }, []);
  
  /**
   * Update an option in the edited question
   */
  const updateOption = useCallback((optionIndex, value) => {
    setEditedQuestion(prev => {
      if (!prev) return null;
      const options = [...prev.options];
      options[optionIndex] = value;
      setHasUnsavedChanges(true);
      return { ...prev, options };
    });
  }, []);
  
  /**
   * Add a new option to the edited question
   */
  const addOption = useCallback(() => {
    setEditedQuestion(prev => {
      if (!prev) return null;
      setHasUnsavedChanges(true);
      return {
        ...prev,
        options: [...prev.options, '']
      };
    });
  }, []);
  
  /**
   * Remove an option from the edited question
   */
  const removeOption = useCallback((optionIndex) => {
    setEditedQuestion(prev => {
      if (!prev || prev.options.length <= 2) return prev; // Keep at least 2 options
      
      const options = prev.options.filter((_, i) => i !== optionIndex);
      
      // Update correct answer indices
      let correctIndices = prev.correctAnswerIndices || [0];
      correctIndices = correctIndices
        .filter(idx => idx !== optionIndex)
        .map(idx => idx > optionIndex ? idx - 1 : idx);
      
      // Ensure at least one correct answer
      if (correctIndices.length === 0) {
        correctIndices = [0];
      }
      
      setHasUnsavedChanges(true);
      return {
        ...prev,
        options,
        correctAnswerIndices: correctIndices
      };
    });
  }, []);
  
  /**
   * Toggle correct answer index
   */
  const toggleCorrectIndex = useCallback((optionIndex, isMultipleChoice = false) => {
    setEditedQuestion(prev => {
      if (!prev) return null;
      
      let correctIndices = [...(prev.correctAnswerIndices || [0])];
      
      if (isMultipleChoice) {
        // Multiple choice: toggle the index
        if (correctIndices.includes(optionIndex)) {
          correctIndices = correctIndices.filter(i => i !== optionIndex);
          // Keep at least one correct
          if (correctIndices.length === 0) {
            correctIndices = [optionIndex];
          }
        } else {
          correctIndices.push(optionIndex);
        }
      } else {
        // Single choice: set only this index
        correctIndices = [optionIndex];
      }
      
      setHasUnsavedChanges(true);
      return {
        ...prev,
        correctAnswerIndices: correctIndices
      };
    });
  }, []);
  
  /**
   * Save the edited question
   */
  const saveEdit = useCallback(() => {
    if (editingIndex === null || !editedQuestion) return false;
    
    setQuestions(prev => {
      const updated = [...prev];
      updated[editingIndex] = editedQuestion;
      return updated;
    });
    
    setEditingIndex(null);
    setEditedQuestion(null);
    setHasUnsavedChanges(false);
    return true;
  }, [editingIndex, editedQuestion, setQuestions]);
  
  /**
   * Cancel editing
   */
  const cancelEdit = useCallback(() => {
    setEditingIndex(null);
    setEditedQuestion(null);
    setHasUnsavedChanges(false);
  }, []);
  
  /**
   * Check if a specific question is being edited
   */
  const isEditingQuestion = useCallback((index) => {
    return editingIndex === index;
  }, [editingIndex]);
  
  return {
    // State
    editingIndex,
    editedQuestion,
    hasUnsavedChanges,
    isEditing: editingIndex !== null,
    
    // Setters
    setEditedQuestion,
    
    // Actions
    startEditing,
    saveEdit,
    cancelEdit,
    
    // Field Updates
    updateEditedQuestion,
    updateField,
    updateOption,
    addOption,
    removeOption,
    toggleCorrectIndex,
    
    // Utilities
    isEditingQuestion
  };
}

export default useQuestionEditor;
